import { COMP_ICON_MAP, COMP_ICON_WEIGHT_MAP } from "@/utils/getCompIconMap";

/**
 * Pure adapter: the EXISTING grouped reports options array → the cascade
 * "sections model" that RootPanel renders. No React, no side effects.
 *
 * Parity by reference (D3): every leaf carries the ORIGINAL option object
 * verbatim as `option`. Selecting a leaf emits `option` unchanged, so the
 * stored widget config and the aggr request body stay byte-identical to the
 * flat picker. We NEVER reconstruct option objects here.
 *
 * Input `options` is whatever the per-slot builder already produces:
 *   - grouped:  [{ display, options:[opt,...] }, ...]
 *   - flat:     [opt, ...]
 *
 * Sections model:
 *   section = { type:'section', label?, rows: Row[] }
 *   Row =
 *     | { type:'folder', key, display, icon, children: Row[], selectOption? }  // folders nest (selectOption = pick whole folder)
 *     | { type:'label',  key, text }                             // in-panel divider label
 *     | { type:'leaf',   key, content:{_id,value,display,desc,icon,iconWeight}, option }
 */

// Identifies the annotations group inside the grouped options array. Must match
// the group display produced by useGetAllOptionsForDimension1.
export const ANNO_GROUP_DISPLAY = "Annotation Tags Based";

const CONNECTED_DATASETS_LABEL = "Connected Datasets";
const TAG_COMPS = ["TagsInputSingle", "TagsInputMulti"];

const isGroupedOptions = (options) =>
  Array.isArray(options) && options.some((o) => o && Array.isArray(o.options));

/**
 * Icon for a reports option, mirroring the V3 filter's getFilterIcon so document
 * fields show the SAME type-based icons as the filter. Reuses the shared
 * COMP_ICON_MAP / COMP_ICON_WEIGHT_MAP (both derived from contentBlockOptions).
 * Returns { icon, iconWeight? } — ListItem reads both.
 */
const getOptionIcon = (opt) => {
  if (!opt) return {};
  if (opt.iconName) return { icon: opt.iconName };

  const vp = opt.valuePath || "";

  // Author / user system field
  if (vp === "meta.kp_contributed_by") return { icon: "User" };

  // Annotation & tag fields → Tag (matches filter's tagType/contentTagType)
  if (
    opt.isAnno ||
    vp.startsWith("tags.") ||
    vp.startsWith("annotations.tags.") ||
    TAG_COMPS.includes(opt.comp)
  ) {
    return { icon: "Tag" };
  }

  // Regular value-path field with a known block comp
  if (opt.comp) {
    return { icon: COMP_ICON_MAP[opt.comp] || "ListDashes", iconWeight: COMP_ICON_WEIGHT_MAP[opt.comp] };
  }

  // Rollup leaf without a comp — derive from rollup type (mirrors getFilterIcon)
  if (opt.rollupType === "tagType") return { icon: "Tag" };
  if (opt.rollupType === "valuePathType") return { icon: "ListDashes" };

  return { icon: "ListDashes" };
};

const toLeaf = (opt) => {
  const { icon, iconWeight } = getOptionIcon(opt);
  return {
    type: "leaf",
    key: opt.value,
    content: {
      _id: opt.value,
      value: opt.value,
      display: opt.display,
      // desc (comp name, or "via A → B" for rollups) hidden for now — the
      // Connected Datasets folder path already conveys the "via" context.
      // Restore by uncommenting.
      // desc: opt.desc,
      icon,
      iconWeight,
      loading: opt.loading || undefined,
    },
    option: opt,
  };
};

// ── Rollups → nested "Connected Datasets" folders ────────────────────────────
// Reports rollup options are fields reachable FROM the source dataset VIA one or
// more connected datasets. The "via" path becomes a folder chain; the field is
// the leaf. At EVERY level, a dataset's own reachable fields render first, then a
// "Connected Datasets" divider, then the deeper connected-dataset folders —
// matching DocHierarchyFilter.

/** The chain of connected-dataset contentTypes a rollup option lives under. */
const getRollupFolderPath = (opt) => {
  if (opt.rollupType === "tagType" && Array.isArray(opt.rollupPath)) {
    return opt.rollupPath.slice(0, -1); // "via" hops = all but the terminal
  }
  if (opt.rollupType === "valuePathType" && opt.tagTypeCollectionToRollup) {
    return [opt.tagTypeCollectionToRollup];
  }
  return [];
};

/** Group rollup options into a nested {folders,leaves} tree by their via-path. */
const buildRollupRoot = (rollups) => {
  const root = { folders: new Map(), leaves: [] };
  for (const opt of rollups) {
    const path = getRollupFolderPath(opt);
    let node = root;
    for (const ct of path) {
      if (!node.folders.has(ct)) node.folders.set(ct, { folders: new Map(), leaves: [] });
      node = node.folders.get(ct);
    }
    node.leaves.push(toLeaf(opt));
  }
  return root;
};

/** Compose a node into rows: [reachable fields…, (Connected Datasets label + deeper folders)?] */
const rollupNodeToRows = (node, rollupDisplayMap, keyPrefix) => {
  const folderRows = [];
  for (const [ct, child] of node.folders) {
    const key = `${keyPrefix}__${ct}`;
    folderRows.push({
      type: "folder",
      key,
      display: rollupDisplayMap[ct] || ct,
      icon: "FolderSimple",
      children: rollupNodeToRows(child, rollupDisplayMap, key),
    });
  }
  const rows = [...node.leaves];
  if (folderRows.length > 0) {
    rows.push({ type: "label", key: `${keyPrefix}__connected`, text: CONNECTED_DATASETS_LABEL });
    rows.push(...folderRows);
  }
  return rows;
};

/**
 * A dataset folder's children: its own fields (leaves), then — recursively at
 * every level — a "Connected Datasets" divider and the nested rollup folders.
 */
const buildDatasetFolderChildren = (options, rollupDisplayMap) => {
  const normal = options.filter((o) => !o.rollupType).map(toLeaf);
  const rollups = options.filter((o) => o.rollupType);
  if (rollups.length === 0) return normal;
  // rollupNodeToRows(root) = [pathless rollup fields…, "Connected Datasets", folders…]
  return [...normal, ...rollupNodeToRows(buildRollupRoot(rollups), rollupDisplayMap, "rollup")];
};

/**
 * The "All Sub-Themes" rollup option groups the chart by sub-theme
 * (rollupType 'tagType', rollupPath ['tags','subThemes']). We clone it and bolt on
 * a preset filter restricting to one role's sub-theme ids to get a single
 * "group by all thematic (or attribute) sub-themes" dimension — no backend change.
 */
const findAllSubThemesRollup = (annoOptions) =>
  annoOptions.find(
    (o) =>
      o.rollupType === "tagType" &&
      Array.isArray(o.rollupPath) &&
      o.rollupPath.length === 2 &&
      o.rollupPath[1] === "subThemes"
  );

/**
 * Folder-level select option: the All-Sub-Themes rollup (bars = sub-themes)
 * narrowed to `roleSubThemes`. Returns undefined when the rollup isn't in this
 * slot's options (e.g. some dim-2 slots) or the role is empty → the folder then
 * stays drill-only (no regression).
 *
 * Two restrictions ride on the option:
 * - `restrictBucketValues` (the CORRECT one): the role's sub-theme ids. The
 *   backend applies a post-rollup `$match` so ONLY these sub-themes bucket — this
 *   is what actually keeps the bars role-pure.
 * - `presetActiveFilters` (graceful fallback): a document-level nestedRollupTagType
 *   filter. On its own it only narrows which annotations are in scope, so
 *   cross-role annotations still leak their other-role sub-themes as bars (that's
 *   the bug `restrictBucketValues` fixes). Kept so behaviour degrades to the
 *   previous state if a backend without the `restrictBucketValues` handling runs.
 */
const buildRoleSelectOption = (allSubThemesRollup, roleSubThemes, label, roleKey) => {
  if (!allSubThemesRollup || roleSubThemes.length === 0) return undefined;
  const roleSubThemeIds = roleSubThemes.map((o) => o.value);
  return {
    ...allSubThemesRollup,
    display: label,
    value: `${allSubThemesRollup.value}__ROLE__${roleKey}`, // unique + stable id
    // Restrict the group-by buckets to this role's sub-themes (backend $match).
    restrictBucketValues: roleSubThemeIds,
    presetActiveFilters: [
      {
        filterId: `activeFilterFrom_ANNO_SUBTHEME_ROLE__${roleKey}`,
        values: roleSubThemeIds.map((id) => ({ value: id })),
        target: {
          filterType: "nestedRollupTagType",
          isAnno: true,
          rollupPath: allSubThemesRollup.rollupPath,
        },
      },
    ],
  };
};

// Topics dimension options (split mode) — see useTopicClusteringDimensionOptions.
const isTopicClusteringOption = (o) => o?.dimensionType === "topicClusteringNode";

/**
 * Split mode's Annotations rows: the primary entry points are the sub-themes
 * themselves (just Signals there) and Topics — the same two the filters
 * sidebar leads with — followed by the whole-level rows (All Tags, …).
 *
 * The Topics folder mirrors the Thematics/Attributes folder contract
 * exactly: its ROW BODY selects "bars = every topic" (selectOption), the
 * chevron drills to one leaf per topic in the clustering tree, and picking a
 * topic charts THAT topic's sub-topics — as picking a sub-theme inside
 * Thematics charts that sub-theme's tags. Nothing is folded by role — roles
 * are a dual-agent concept.
 */
const buildSplitModeAnnoRows = (annoOptions) => {
  const subThemes = annoOptions.filter((o) => o.isAnnoSubTheme);
  const topicOptions = annoOptions.filter(isTopicClusteringOption);
  // Sub-theme skeleton rows only; the topic drills' skeletons carry the
  // dimensionType and land inside the Topics folder.
  const loadingRows = annoOptions.filter((o) => o.loading && !isTopicClusteringOption(o));
  const rest = annoOptions.filter(
    (o) => !o.isAnnoSubTheme && !isTopicClusteringOption(o) && !o.loading
  );

  const topicsRoot = topicOptions.find((o) => o.level === "topic" && !o.parentTopicNodeId);
  const topicDrills = topicOptions.filter((o) => o !== topicsRoot);
  const topicsFolder = topicsRoot
    ? {
        type: "folder",
        key: "__anno_topics",
        display: "Topics",
        icon: "ShareNetwork",
        children: topicDrills.map(toLeaf),
        selectOption: topicsRoot,
      }
    : null;

  return [
    ...loadingRows.map(toLeaf),
    ...subThemes.map(toLeaf),
    ...(topicsFolder ? [topicsFolder] : []),
    ...rest.map(toLeaf),
  ];
};

/**
 * Build the rows for the Annotations section.
 * - Split mode (`isSplitMode`): buildSplitModeAnnoRows — sub-themes + Topics
 *   lead, no role folders.
 * - When `annoSplitEnabled` (self-managed + non-legacy anno mode, read by RootPanel):
 *   split the sub-theme options (those stamped `isAnnoSubTheme`) into Thematics /
 *   Attributes folders by their own `taxonomyRole`, keeping every remaining anno
 *   option as a sibling leaf ("keep alongside"). Each folder also carries a
 *   `selectOption` — the dimension emitted when the folder ROW itself is selected
 *   ("group by all sub-themes of this role"); the chevron still drills to a single
 *   sub-theme (see FolderRow). Drill-picking a sub-theme is byte-identical to the
 *   flat picker; selecting the folder is a new, additive capability.
 * - Otherwise: a plain flat list of all anno options as leaves.
 */
const buildAnnoRows = (annoOptions, annoSplitEnabled, isSplitMode) => {
  if (isSplitMode) return buildSplitModeAnnoRows(annoOptions);
  const subThemes = annoOptions.filter((o) => o.isAnnoSubTheme);

  if (!annoSplitEnabled || subThemes.length === 0) {
    // Split enabled but sub-themes still loading: the grouped skeleton rows arrive
    // BETWEEN "Tags" and "Sub-Themes" in source order, which reads oddly. Float them
    // to the top so they occupy where the Thematics/Attributes folders will land
    // once loaded — keeps the "All …" siblings together and avoids a layout jump.
    if (annoSplitEnabled) {
      const loadingRows = annoOptions.filter((o) => o.loading);
      if (loadingRows.length > 0) {
        return [...loadingRows, ...annoOptions.filter((o) => !o.loading)].map(toLeaf);
      }
    }
    return annoOptions.map(toLeaf);
  }

  const subThemeValues = new Set(subThemes.map((o) => o.value));
  // Keep the raw role partitions — their ids feed the folder-select preset filter.
  const thematicSubThemes = subThemes.filter((o) => o.taxonomyRole !== "attribute");
  const attributeSubThemes = subThemes.filter((o) => o.taxonomyRole === "attribute");
  const thematicChildren = thematicSubThemes.map(toLeaf);
  const attributeChildren = attributeSubThemes.map(toLeaf);

  // KEEP: whole-level anno rows (All Tags / All Sub-Themes / All Themes). Comment
  // out this line + its spread below to hide them once folder-select is trusted.
  const siblingLeaves = annoOptions.filter((o) => !subThemeValues.has(o.value)).map(toLeaf);

  const allSubThemesRollup = findAllSubThemesRollup(annoOptions);

  const folders = [];
  if (thematicChildren.length > 0) {
    folders.push({
      type: "folder",
      key: "__anno_thematics",
      display: "Thematics",
      icon: "Tag",
      children: thematicChildren,
      selectOption: buildRoleSelectOption(allSubThemesRollup, thematicSubThemes, "Thematics", "thematic"),
    });
  }
  if (attributeChildren.length > 0) {
    folders.push({
      type: "folder",
      key: "__anno_attributes",
      display: "Attributes",
      icon: "Tag",
      children: attributeChildren,
      selectOption: buildRoleSelectOption(allSubThemesRollup, attributeSubThemes, "Attributes", "attribute"),
    });
  }

  return [...folders, ...siblingLeaves];
};

export const buildCascadeTree = (options = [], { rollupDisplayMap = {}, annoSplitEnabled = false, isSplitMode = false } = {}) => {
  if (!isGroupedOptions(options)) {
    return [
      { type: "section", label: undefined, rows: buildDatasetFolderChildren(options || [], rollupDisplayMap) },
    ];
  }

  const annoGroup = options.find((g) => g.display === ANNO_GROUP_DISPLAY);
  const datasetGroups = options.filter((g) => g !== annoGroup);

  // Single dataset group and no annotations → auto-flatten (no pointless folder),
  // but still surface Connected Datasets for that one dataset.
  // A group carrying a `selectOption` is exempt: there the folder row IS the
  // selectable thing, so flattening it away would delete the option entirely.
  if (!annoGroup && datasetGroups.length === 1 && !datasetGroups[0].selectOption) {
    return [
      { type: "section", label: undefined, rows: buildDatasetFolderChildren(datasetGroups[0].options || [], rollupDisplayMap) },
    ];
  }

  const sections = [];

  if (annoGroup) {
    sections.push({
      type: "section",
      label: "Annotations",
      rows: buildAnnoRows(annoGroup.options || [], annoSplitEnabled, isSplitMode),
    });
  }

  // A dataset group may carry its own `selectOption` — the option emitted when the
  // folder ROW BODY is picked, while the chevron still drills into its fields
  // (FolderRow → PanelItem does the split). Groups without one stay drill-only,
  // which is every widget's picker today.
  const datasetRows = datasetGroups.map((g) => ({
    type: "folder",
    key: g.display,
    display: g.display,
    icon: "FolderSimple",
    children: buildDatasetFolderChildren(g.options || [], rollupDisplayMap),
    selectOption: g.selectOption,
  }));

  sections.push({
    type: "section",
    label: annoGroup ? "Datasets" : undefined,
    rows: datasetRows,
  });

  return sections;
};

/**
 * Folder keys on the path from a section root down to the currently-selected
 * leaf (matched by option.value). Used to mark ancestor folders as "contains
 * your selection". Returns an empty Set when nothing is selected or the value
 * isn't in the tree.
 */
export const getSelectedPathKeys = (sections = [], selectedValue) => {
  if (!selectedValue) return new Set();
  const walk = (rows, path) => {
    for (const row of rows || []) {
      if (row.type === "leaf") {
        if (row.option?.value === selectedValue || row.key === selectedValue) return path;
      } else if (row.type === "folder") {
        // A selectable folder IS the selection (its whole-role payload) → mark its
        // ancestors. FolderRow highlights the folder itself; this covers nesting.
        if (row.selectOption?.value === selectedValue) return path;
        const found = walk(row.children, [...path, row.key]);
        if (found) return found;
      }
    }
    return null;
  };
  for (const section of sections) {
    const found = walk(section.rows, []);
    if (found) return new Set(found);
  }
  return new Set();
};
